<template>
  <div v-if="loading && !disabled">
    <eg-b-skeleton width="25%"></eg-b-skeleton>
    <eg-b-skeleton type="input"></eg-b-skeleton>
  </div>
  <div v-else>
    <eg-selectable-table
      v-if="bulk"
      :value="selectedIds"
      :items="assignments"
      :total="assignments.length"
      :limit="assignments.length"
      :fields="fields"
      @input="selectAssignments"
    >
      <template #selectAll="{ selectedAll, selectAll }">
        <eg-b-form-checkbox
          :checked="selectedAll"
          variant="info"
          :indeterminate="
            selectedIds.length > 0 && selectedIds.length < assignments.length
          "
          @change="selectAll"
        >
          {{ $t("imports.selectAllAssignments") }}
        </eg-b-form-checkbox>
      </template>
    </eg-selectable-table>
    <eg-form-group v-else :label="$t('imports.selectAssignment')">
      <eg-search-select
        entity="Assignment"
        title-key="title"
        :items="assignments"
        :value="getSelectedAssignment()"
        menu-class="w-100"
        @input="selectAssignments([$event.id])"
      >
        <template #item="{ item }">
          <slot v-if="$scopedSlots.item" name="item" :assignment="item"></slot>
          <template v-else>
            <div class="text-truncate">
              {{ item.title }}
            </div>
          </template>
        </template>
        <template #no-items>
          <div>
            The selected classroom has no assignments. Choose a different
            classroom to continue.
          </div>
        </template>
      </eg-search-select>
    </eg-form-group>
  </div>
</template>

<script>
import importApi from "@/api/requests/import";
import errorHandler from "./errorHandler";
import EgFormGroup from "@/components/global/eg_form_group.vue";
import EgSelectableTable from "@/components/global/eg_selectable_table";
import EgSearchSelect from "@/components/global/eg_search_select.vue";

export default {
  components: {
    EgSearchSelect,
    EgFormGroup,
    EgSelectableTable,
  },

  props: {
    value: {
      type: Array,
      default: () => {},
    },

    classIds: {
      type: Array,
      required: true,
    },

    bulk: {
      type: Boolean,
      default: false,
    },
    platformKey: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: true,
      selectedIds: [],
      assignments: [],
      fields: [
        {
          key: "title",
          label: this.$t("common.assignment"),
          formatter: (value, key, item) => {
            return item.title;
          },
        },
        {
          key: "class",
          label: this.$t("common.class"),
          formatter: (value, key, item) => {
            return item.class.name;
          },
        },
      ],
    };
  },
  watch: {
    classIds: {
      immediate: true,
      handler() {
        this.init();
      },
    },
  },
  methods: {
    getSelectedAssignment() {
      return (
        this.assignments.find(
          (assignment) => assignment.id === this.value[0]?.id
        ) || null
      );
    },

    selectAssignments(selected) {
      this.selectedIds = selected;
      const selectedAssignments = this.assignments.filter((assignment) =>
        this.selectedIds.includes(assignment.id)
      );
      this.$emit("input", selectedAssignments);
      const assignmentDefaultRubricIds = selectedAssignments
        .filter((assignment) => assignment.defaultRubricId)
        .map((assignment) => assignment.defaultRubricId);
      assignmentDefaultRubricIds.length &&
        this.$emit("setAssignmentDefaultRubricId", assignmentDefaultRubricIds);
    },

    async init() {
      try {
        if (this.classIds.length === 0) {
          return;
        }
        this.loading = true;
        const method = this.bulk ? "getBulkAssignments" : "getAssignments";
        const { data } = await importApi[this.platformKey][method](
          this.id,
          this.classIds
        );
        this.assignments = data.assignments;

        const firstAssignmentId = this.assignments[0]?.id;
        let selectedAssignmentIds = firstAssignmentId
          ? [firstAssignmentId]
          : [];

        if (this.value) {
          const valueIds = this.value.map((assignment) => assignment.id);
          const assignmentIds = this.assignments
            .filter((assignment) => valueIds.includes(assignment.id))
            .map((assignment) => assignment.id);

          if (assignmentIds) {
            selectedAssignmentIds = assignmentIds;
          }
        }

        selectedAssignmentIds && this.selectAssignments(selectedAssignmentIds);
        this.loading = false;
        this.$emit("loaded");
      } catch (error) {
        this.loading = false;
        this.$emit("loaded");
        errorHandler({
          vm: this,
          error,
          message:
            "There was a problem fetching assignments for the classroom. Please try again",
        });
      }
    },
  },
};
</script>
